import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  Plus,
  Inbox,
  Loader2,
  Pencil,
  Trash2,
  Link2,
  ShieldCheck,
  ShieldOff,
} from 'lucide-react';
import { leadIntakeFormsAPI } from '../utils/api';
import ConfirmModal from '../components/ConfirmModal';
import { usePermissions } from '../hooks/usePermissions';

function formatDate(iso?: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('it-IT');
}

export default function LeadIntakeForms() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { hasPermission } = usePermissions();
  const canEdit = hasPermission('leads:config:edit');
  const [page, setPage] = useState(1);
  const [deleteTarget, setDeleteTarget] = useState<{ id: string; name: string } | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['lead-intake-forms', page],
    queryFn: async () => {
      const res = await leadIntakeFormsAPI.getForms({ page, limit: 20 });
      return res.data;
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => leadIntakeFormsAPI.deleteForm(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['lead-intake-forms'] });
      toast.success('Form eliminato');
      setDeleteTarget(null);
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.detail || "Errore nell'eliminazione");
    },
  });

  const forms = data?.data || [];
  const pagination = data?.pagination;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Form pubblici Lead</h1>
          <p className="text-gray-600">
            Form web embeddabili che generano lead nel tuo tenant
          </p>
        </div>
        {canEdit && (
          <button
            onClick={() => navigate('/leads/intake-forms/new')}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            <Plus className="h-5 w-5" />
            Nuovo form
          </button>
        )}
      </div>

      {/* List */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        {isLoading ? (
          <div className="flex justify-center items-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
          </div>
        ) : forms.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            <Inbox className="h-12 w-12 mx-auto mb-4 text-gray-300" />
            <p>Nessun form creato</p>
            {canEdit && (
              <button
                onClick={() => navigate('/leads/intake-forms/new')}
                className="mt-4 inline-flex items-center gap-2 text-blue-600 hover:text-blue-800 text-sm font-medium"
              >
                <Plus className="h-4 w-4" />
                Crea il primo form
              </button>
            )}
          </div>
        ) : (
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Nome
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Slug
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Stato
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Rate limit
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Captcha
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Submissions 30gg
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Creato
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Azioni
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {forms.map((form) => (
                <tr
                  key={form.id}
                  className={canEdit ? 'hover:bg-gray-50 cursor-pointer' : 'hover:bg-gray-50'}
                  onClick={
                    canEdit
                      ? () => navigate(`/leads/intake-forms/${form.id}/edit`)
                      : undefined
                  }
                >
                  <td className="px-6 py-4">
                    <div className="text-sm font-medium text-gray-900">{form.titolo}</div>
                  </td>
                  <td className="px-6 py-4">
                    <code className="inline-flex items-center gap-1 text-xs bg-gray-100 text-gray-700 px-2 py-0.5 rounded">
                      <Link2 className="h-3 w-3" />
                      {form.slug}
                    </code>
                  </td>
                  <td className="px-6 py-4">
                    {form.is_active ? (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">
                        <ShieldCheck className="h-3 w-3" />
                        Attivo
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-500">
                        <ShieldOff className="h-3 w-3" />
                        Disattivo
                      </span>
                    )}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-700">
                    {form.rate_limit_per_hour}/ora
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-700">
                    {form.captcha_provider ? (
                      <span className="text-green-700 font-medium">{form.captcha_provider}</span>
                    ) : (
                      <span className="text-gray-400">—</span>
                    )}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-900 font-semibold">
                    {form.submissions_30d}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-500">
                    {formatDate(form.created_at)}
                  </td>
                  <td className="px-6 py-4 text-right">
                    {canEdit && (
                      <div className="inline-flex items-center gap-1">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/leads/intake-forms/${form.id}/edit`);
                          }}
                          className="p-1 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded"
                          title="Modifica"
                        >
                          <Pencil className="h-4 w-4" />
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setDeleteTarget({ id: form.id, name: form.titolo });
                          }}
                          className="p-1 text-red-600 hover:text-red-800 hover:bg-red-50 rounded"
                          title="Elimina"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {pagination && pagination.pages > 1 && (
          <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200">
            <div className="text-sm text-gray-700">
              Pagina {pagination.page} di {pagination.pages} ({pagination.total} form)
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => setPage(page - 1)}
                disabled={page === 1}
                className="px-3 py-1 border border-gray-300 rounded-md disabled:opacity-50"
              >
                Precedente
              </button>
              <button
                onClick={() => setPage(page + 1)}
                disabled={page >= pagination.pages}
                className="px-3 py-1 border border-gray-300 rounded-md disabled:opacity-50"
              >
                Successivo
              </button>
            </div>
          </div>
        )}
      </div>

      {deleteTarget && (
        <ConfirmModal
          isOpen={!!deleteTarget}
          title="Elimina form pubblico"
          message={`Eliminare il form "${deleteTarget.name}"? Lo slug sarà liberato e il form non accetterà più submission.`}
          confirmLabel="Elimina"
          onConfirm={() => deleteMutation.mutate(deleteTarget.id)}
          onClose={() => setDeleteTarget(null)}
          isLoading={deleteMutation.isPending}
          variant="danger"
        />
      )}
    </div>
  );
}
